'use client';

import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { supabase } from '@/lib/supabase/client';
import {
  initPostHog,
  capture,
  capturePageview,
  identifyResearchUser,
  resetPostHog,
  type ResearchEvent,
} from '@/lib/posthog';

/**
 * PostHogProvider — initializes PostHog once, identifies on the Supabase user
 * UUID when a shared session exists, and (via the Suspense-isolated child)
 * captures a `$pageview` on every App Router route change.
 *
 * The research site shares Supabase localStorage with the app (same origin),
 * so a user already logged into the Aviado app is identifiable here — that's
 * what stitches their research browsing to their app person.
 *
 * The pageview tracker uses `useSearchParams()`, which forces dynamic
 * rendering unless wrapped in Suspense — isolating it here keeps all 270
 * article pages statically generated (same guard the browse page uses).
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initPostHog();

    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      const id = data.session?.user?.id;
      if (id) identifyResearchUser(id);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user?.id) identifyResearchUser(session.user.id);
      else if (event === 'SIGNED_OUT') resetPostHog();
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Delegated link-click capture — lets SERVER components (which can't carry
  // onClick) be instrumented with plain data attributes:
  //   <Link data-ph-event="cross_link_clicked" data-ph-target_type="pillar"
  //         data-ph-slug="brain-cognitive" …>
  // Any `data-ph-*` (besides `event`) becomes an event property. One listener
  // covers the whole tree, so cross-links, hub tiles, recirculation, TOC, etc.
  // need only markup, no client conversion.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-ph-event]');
      if (!el) return;
      const props: Record<string, string> = {};
      for (const { name, value } of Array.from(el.attributes)) {
        if (name.startsWith('data-ph-') && name !== 'data-ph-event') {
          props[name.slice('data-ph-'.length).replace(/-/g, '_')] = value;
        }
      }
      capture(el.dataset.phEvent as ResearchEvent, props);
    }
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PageviewTracker />
      </Suspense>
      {children}
    </>
  );
}

function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (pathname) capturePageview(pathname);
    // searchParams included so filtered/searched URLs count as distinct views.
  }, [pathname, searchParams]);

  return null;
}

export default PostHogProvider;
